import { MountayaProvider } from "@mountaya/client-react";
import { createContext, type ReactNode, use, useMemo } from "react";

import type { AuthStrategy } from "../rules/auth";
import type { RestClientConfig } from "../rules/sdk";
import { mountayaSurfaces } from "../rules/sdk";

/**
 * One credential, delivered to every hook beneath it.
 *
 * Before this, each of the application's hooks took a `RestClientConfig` as its first
 * argument and every call site built one inline. That is not merely verbose: it makes the
 * credential a per-call-site decision in a tree where it is really a per-SUBTREE fact. A
 * studio component reaching for `{ baseUrl: env.apiBaseUrl }` inside an embed preview gets
 * the signed-in user's cookie instead of the embed's publishable key, reads a resource it
 * should not see, and reports success. Nesting a provider makes the right answer the
 * default and the wrong one unspellable.
 *
 * Two contexts rather than one, because they answer different questions:
 *
 * - `@mountaya/client-react`'s `MountayaProvider` carries the constructed `Mountaya`. That
 *   is the published seam, and adopting it is what makes `useMountaya` and the SDK's own
 *   credential-scope binding available to this tree exactly as they are to a customer's. It
 *   carries ONE client reaching all three services, because the reads beneath it do: a studio
 *   dispatch writes over REST and re-reads the Data API, and the viewer beside it registers a
 *   tile specification. A client per service meant a credential mapping per service, and a tile
 *   credential that attaches to the wrong origin 401s every tile silently — which reads as an
 *   itinerary with no geometry rather than as an error.
 * - The config context beside it carries the PRE-transport `AuthStrategy`. The SDK's
 *   provider deliberately holds a client, and a client has already validated and swallowed
 *   its credential — but the operations the contract never publishes take a config rather
 *   than a client, because they name their own response type off the generated document and
 *   reach the API through `transport.request<T>` on whichever client the config resolves to.
 *
 * What does NOT move here: the render-free callers. A route loader calling `ensureQueryData`
 * runs outside React and builds its own client for the credential it holds, which is what
 * lets a share link's prefetch land on the entry the surface beneath it reads.
 */

/** The strategy and base URL the nearest provider was built with. */
interface ClientConfigValue {
  readonly config: RestClientConfig;
}

const ClientConfigContext = createContext<ClientConfigValue | null>(null);

export interface MountayaClientProviderProps {
  /** The REST API's base URL. */
  readonly baseUrl: string;
  /** The Data API's base URL, for the subtrees whose tools re-read what they wrote. */
  readonly dataUrl?: string;
  /** The tile service's base URL, for the subtrees that register a scoped specification. */
  readonly tilesUrl?: string;
  /**
   * How requests authenticate beneath this provider. Omitted at the application root, where
   * the Ory session cookie is the credential; set by the embed and share surfaces, whose
   * subtrees must never fall back to it.
   */
  readonly auth?: AuthStrategy;
  readonly children: ReactNode;
}

export const MountayaClientProvider = ({
  auth,
  baseUrl,
  children,
  dataUrl,
  tilesUrl,
}: MountayaClientProviderProps) => {
  // Memoized on the two primitives rather than on a config object, so a parent re-render
  // that rebuilds the object literal does not rebuild the context value and re-render every
  // consumer beneath it. `mountayaFor` caches the client itself, so the identity that
  // matters here is the context value's.
  const value = useMemo<ClientConfigValue>(
    () => ({ config: auth === undefined ? { baseUrl } : { auth, baseUrl } }),
    [auth, baseUrl],
  );

  // `mountayaSurfaces` caches on the urls and the credential, so this is one client per
  // distinct subtree rather than one per render.
  const client = mountayaSurfaces({ auth, dataUrl, restUrl: baseUrl, tilesUrl });

  return (
    <ClientConfigContext value={value}>
      <MountayaProvider client={client}>{children}</MountayaProvider>
    </ClientConfigContext>
  );
};

/**
 * useRestClientConfig reads the nearest provider's configuration.
 *
 * Throws outside a provider rather than returning a default. A hook that silently fell back
 * to the session cookie would turn a missing provider into a request made with the wrong
 * credential — which succeeds, returns real data, and is exactly the failure this provider
 * exists to remove.
 */
export const useRestClientConfig = (): RestClientConfig => {
  const value = use(ClientConfigContext);
  if (value === null) {
    throw new Error(
      "useRestClientConfig must be used within a <MountayaClientProvider>. Wrap the tree at " +
        "the application root, and again inside any surface that authenticates differently.",
    );
  }

  return value.config;
};
